import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { authAPI, usersAPI } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import type { EmailChangeRequest } from '../types';

const profileSchema = z.object({
  nome: z.string().min(1, 'Il nome è obbligatorio').max(100),
  cognome: z.string().min(1, 'Il cognome è obbligatorio').max(100),
  telefono: z.string().optional().or(z.literal('')),
});

type ProfileFormData = z.infer<typeof profileSchema>;

const passwordSchema = z.object({
  current_password: z.string().min(1, 'Inserisci la password attuale'),
  new_password: z
    .string()
    .min(8, 'Minimo 8 caratteri')
    .regex(/[A-Z]/, 'Deve contenere almeno una lettera maiuscola')
    .regex(/[a-z]/, 'Deve contenere almeno una lettera minuscola')
    .regex(/[0-9]/, 'Deve contenere almeno un numero'),
  confirm_password: z.string().min(1, 'Conferma la nuova password'),
}).refine((data) => data.new_password === data.confirm_password, {
  message: 'Le password non corrispondono',
  path: ['confirm_password'],
});

type PasswordFormData = z.infer<typeof passwordSchema>;

const emailChangeSchema = z
  .object({
    new_email: z
      .string()
      .min(1, 'La nuova email è obbligatoria')
      .email('Formato email non valido'),
    confirm_email: z.string().min(1, 'Conferma la nuova email'),
    current_password: z.string().min(1, 'Inserisci la password attuale'),
  })
  .refine((data) => data.new_email.trim().toLowerCase() === data.confirm_email.trim().toLowerCase(), {
    message: 'Le email non coincidono',
    path: ['confirm_email'],
  });

type EmailChangeFormData = z.infer<typeof emailChangeSchema>;

const mapEmailChangeError = (error: any): string => {
  const status = error?.response?.status;
  const detail = error?.response?.data?.detail;
  if (status === 429 || detail === 'rate_limit_exceeded') {
    return 'Troppe richieste: attendi prima di riprovare.';
  }
  if (status === 422) {
    return 'Formato email non valido. Controlla il nuovo indirizzo.';
  }
  return 'Errore durante la richiesta di cambio email. Riprova.';
};

const roleLabels: Record<string, string> = {
  admin: 'Amministratore',
  manager: 'Manager',
  commerciale: 'Commerciale',
  viewer: 'Visualizzatore',
  cliente: 'Cliente',
};

const EyeIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
  </svg>
);

const EyeSlashIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
  </svg>
);

const MyProfile: React.FC = () => {
  const { user, refreshToken } = useAuth();
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [pendingEmailChange, setPendingEmailChange] = useState<{
    email: string;
    payload: EmailChangeRequest;
  } | null>(null);
  const [emailChangeError, setEmailChangeError] = useState<string | null>(null);

  const {
    register: registerProfile,
    handleSubmit: handleProfileSubmit,
    reset: resetProfile,
    formState: { errors: profileErrors, isDirty: isProfileDirty },
  } = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
  });

  const {
    register: registerPassword,
    handleSubmit: handlePasswordSubmit,
    reset: resetPassword,
    formState: { errors: passwordErrors },
  } = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
  });

  const {
    register: registerEmailChange,
    handleSubmit: handleEmailChangeSubmit,
    reset: resetEmailChange,
    setError: setEmailChangeFieldError,
    formState: { errors: emailChangeErrors },
  } = useForm<EmailChangeFormData>({
    resolver: zodResolver(emailChangeSchema),
  });

  useEffect(() => {
    if (user) {
      resetProfile({
        nome: user.nome || '',
        cognome: user.cognome || '',
        telefono: user.telefono || '',
      });
    }
  }, [user, resetProfile]);

  const profileMutation = useMutation({
    mutationFn: (data: ProfileFormData) => usersAPI.updateMyProfile(data),
    onSuccess: () => {
      toast.success('Profilo aggiornato');
      refreshToken();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore durante il salvataggio');
    },
  });

  const passwordMutation = useMutation({
    mutationFn: (data: PasswordFormData) =>
      usersAPI.changePassword(user!.id, {
        current_password: data.current_password,
        new_password: data.new_password,
      }),
    onSuccess: () => {
      toast.success('Password modificata con successo');
      resetPassword();
      setShowNewPassword(false);
      setShowConfirmPassword(false);
    },
    onError: (error: any) => {
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : 'Errore durante il cambio password';
      toast.error(message);
    },
  });

  const onProfileSubmit = (data: ProfileFormData) => {
    profileMutation.mutate(data);
  };

  const onPasswordSubmit = (data: PasswordFormData) => {
    passwordMutation.mutate(data);
  };

  const emailChangeMutation = useMutation({
    mutationFn: (data: EmailChangeRequest) => authAPI.requestEmailChange(data),
    retry: false,
    onSuccess: (_response, variables) => {
      setEmailChangeError(null);
      setPendingEmailChange({ email: variables.new_email, payload: variables });
      resetEmailChange();
      setShowCurrentPassword(false);
    },
    onError: (error: any) => {
      setEmailChangeError(mapEmailChangeError(error));
    },
  });

  const onEmailChangeSubmit = (data: EmailChangeFormData) => {
    if (user?.email && data.new_email.trim().toLowerCase() === user.email.toLowerCase()) {
      setEmailChangeFieldError('new_email', {
        type: 'manual',
        message: 'La nuova email deve essere diversa da quella attuale',
      });
      return;
    }
    setEmailChangeError(null);
    emailChangeMutation.mutate({
      new_email: data.new_email.trim(),
      current_password: data.current_password,
    });
  };

  const inputClass =
    'block w-full border border-gray-300 rounded-md text-sm py-2 px-3 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500';
  const readOnlyClass =
    'block w-full bg-gray-50 border border-gray-200 rounded-md text-sm py-2 px-3 text-gray-600';

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">I Miei Dati</h1>
        <p className="text-gray-600 mt-1">Gestisci le tue informazioni personali e la password</p>
      </div>

      {/* Profile Section */}
      <form onSubmit={handleProfileSubmit(onProfileSubmit)}>
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Dati Personali</h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Read-only fields */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input type="text" value={user?.email || ''} readOnly className={readOnlyClass} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ruolo</label>
              <input
                type="text"
                value={roleLabels[user?.role || ''] || user?.role || ''}
                readOnly
                className={readOnlyClass}
              />
            </div>
            {user?.ultimo_accesso && (
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ultimo accesso
                </label>
                <input
                  type="text"
                  value={new Date(user.ultimo_accesso).toLocaleString('it-IT')}
                  readOnly
                  className={readOnlyClass}
                />
              </div>
            )}

            {/* Editable fields */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
              <input type="text" {...registerProfile('nome')} className={inputClass} />
              {profileErrors.nome && (
                <p className="mt-1 text-sm text-red-600">{profileErrors.nome.message}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cognome</label>
              <input type="text" {...registerProfile('cognome')} className={inputClass} />
              {profileErrors.cognome && (
                <p className="mt-1 text-sm text-red-600">{profileErrors.cognome.message}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Telefono</label>
              <input type="text" {...registerProfile('telefono')} className={inputClass} />
            </div>
          </div>

          <div className="mt-6 flex justify-end">
            <button
              type="submit"
              disabled={!isProfileDirty || profileMutation.isPending}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {profileMutation.isPending ? 'Salvataggio...' : 'Salva Modifiche'}
            </button>
          </div>
        </div>
      </form>

      {/* Email Change Section */}
      <form onSubmit={handleEmailChangeSubmit(onEmailChangeSubmit)} noValidate>
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Cambio Email</h3>

          <p className="text-sm text-gray-600 mb-4">
            L'indirizzo attuale <span className="font-medium text-gray-900">{user?.email}</span>{' '}
            resta attivo finché non confermi il nuovo indirizzo tramite il link che riceverai via
            email.
          </p>

          {pendingEmailChange && (
            <div
              className="mb-4 rounded-md bg-blue-50 border border-blue-200 p-4"
              aria-live="polite"
            >
              <p className="text-sm text-blue-800">
                Verifica in sospeso per{' '}
                <span className="font-medium">{pendingEmailChange.email}</span>: se la richiesta è
                valida, riceverai le istruzioni di verifica al nuovo indirizzo. Il cambio diventa
                effettivo solo dopo la conferma.
              </p>
              <div className="mt-3 flex gap-3">
                <button
                  type="button"
                  onClick={() => emailChangeMutation.mutate(pendingEmailChange.payload)}
                  disabled={emailChangeMutation.isPending}
                  className="inline-flex items-center px-3 py-1.5 border border-blue-300 text-sm font-medium rounded-md text-blue-700 bg-white hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {emailChangeMutation.isPending ? 'Invio...' : 'Invia di nuovo la verifica'}
                </button>
                <button
                  type="button"
                  onClick={() => setPendingEmailChange(null)}
                  className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-blue-700 hover:text-blue-900"
                >
                  Chiudi avviso
                </button>
              </div>
            </div>
          )}

          {emailChangeError && (
            <div
              className="mb-4 rounded-md bg-red-50 border border-red-200 p-4"
              role="alert"
            >
              <p className="text-sm text-red-700">{emailChangeError}</p>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="new_email" className="block text-sm font-medium text-gray-700 mb-1">
                Nuova email
              </label>
              <input
                id="new_email"
                type="email"
                autoComplete="email"
                aria-invalid={!!emailChangeErrors.new_email}
                aria-describedby={emailChangeErrors.new_email ? 'new_email-error' : undefined}
                {...registerEmailChange('new_email')}
                className={inputClass}
              />
              {emailChangeErrors.new_email && (
                <p id="new_email-error" className="mt-1 text-sm text-red-600">
                  {emailChangeErrors.new_email.message}
                </p>
              )}
            </div>
            <div>
              <label
                htmlFor="confirm_email"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Conferma nuova email
              </label>
              <input
                id="confirm_email"
                type="email"
                autoComplete="off"
                aria-invalid={!!emailChangeErrors.confirm_email}
                aria-describedby={
                  emailChangeErrors.confirm_email ? 'confirm_email-error' : undefined
                }
                {...registerEmailChange('confirm_email')}
                className={inputClass}
              />
              {emailChangeErrors.confirm_email && (
                <p id="confirm_email-error" className="mt-1 text-sm text-red-600">
                  {emailChangeErrors.confirm_email.message}
                </p>
              )}
            </div>
            <div className="sm:col-span-2">
              <label
                htmlFor="email_current_password"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Password attuale
              </label>
              <div className="relative">
                <input
                  id="email_current_password"
                  type={showCurrentPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  aria-invalid={!!emailChangeErrors.current_password}
                  aria-describedby={
                    emailChangeErrors.current_password ? 'email_current_password-error' : undefined
                  }
                  {...registerEmailChange('current_password')}
                  className={`${inputClass} pr-10`}
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  aria-label={showCurrentPassword ? 'Nascondi password' : 'Mostra password'}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showCurrentPassword ? <EyeSlashIcon /> : <EyeIcon />}
                </button>
              </div>
              {emailChangeErrors.current_password && (
                <p id="email_current_password-error" className="mt-1 text-sm text-red-600">
                  {emailChangeErrors.current_password.message}
                </p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Per sicurezza serve la password attuale. Per account collegati a Google il cambio
                email non è disponibile.
              </p>
            </div>
          </div>

          <div className="mt-6 flex justify-end">
            <button
              type="submit"
              disabled={emailChangeMutation.isPending}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {emailChangeMutation.isPending ? 'Invio in corso...' : 'Richiedi Cambio Email'}
            </button>
          </div>
        </div>
      </form>

      {/* Password Section */}
      <form onSubmit={handlePasswordSubmit(onPasswordSubmit)}>
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Cambio Password</h3>

          <label className="block text-sm font-medium text-gray-700 mb-4">Password attuale per il cambio password
            <input type="password" autoComplete="current-password" {...registerPassword('current_password')} className={inputClass} />
            {passwordErrors.current_password && <span className="text-red-600">{passwordErrors.current_password.message}</span>}
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nuova password
              </label>
              <div className="relative">
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  {...registerPassword('new_password')}
                  className={`${inputClass} pr-10`}
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showNewPassword ? <EyeSlashIcon /> : <EyeIcon />}
                </button>
              </div>
              {passwordErrors.new_password && (
                <p className="mt-1 text-sm text-red-600">{passwordErrors.new_password.message}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Conferma nuova password
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  {...registerPassword('confirm_password')}
                  className={`${inputClass} pr-10`}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showConfirmPassword ? <EyeSlashIcon /> : <EyeIcon />}
                </button>
              </div>
              {passwordErrors.confirm_password && (
                <p className="mt-1 text-sm text-red-600">
                  {passwordErrors.confirm_password.message}
                </p>
              )}
            </div>
          </div>

          <p className="mt-3 text-xs text-gray-500">
            La password deve contenere almeno 8 caratteri, una lettera maiuscola, una minuscola e un numero.
          </p>

          <div className="mt-6 flex justify-end">
            <button
              type="submit"
              disabled={passwordMutation.isPending}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {passwordMutation.isPending ? 'Aggiornamento...' : 'Cambia Password'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default MyProfile;
